import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const EditProjectDialog = ({ isEditDialogOpen, setIsEditDialogOpen, editingProject, setEditingProject, handleUpdateProject, updating, customers, designers, subcontractors }) => {
    const [showProjectTypeConfirm, setShowProjectTypeConfirm] = React.useState(false);
    const [pendingProjectType, setPendingProjectType] = React.useState(null);
    const [originalProjectType, setOriginalProjectType] = React.useState(null);
    
    // Track original project type when dialog opens
    React.useEffect(() => {
        if (isEditDialogOpen && editingProject) {
            setOriginalProjectType(editingProject.project_type);
        }
    }, [isEditDialogOpen, editingProject]);
    
    if (!editingProject) return null;

    // Helper for date formatting
    const formatDate = (dateString) => dateString ? dateString.split('T')[0] : "";

    // Handle project type change with confirmation
    const handleProjectTypeChange = (value) => {
        if (originalProjectType && value !== originalProjectType) {
            // Show confirmation dialog
            setPendingProjectType(value);
            setShowProjectTypeConfirm(true);
        } else {
            // No change or first selection
            setEditingProject({ ...editingProject, project_type: value });
        }
    };

    const confirmProjectTypeChange = () => {
        setEditingProject({ ...editingProject, project_type: pendingProjectType });
        setShowProjectTypeConfirm(false);
        setPendingProjectType(null);
    };

    const cancelProjectTypeChange = () => {
        setShowProjectTypeConfirm(false);
        setPendingProjectType(null);
    };

    return (
        <>
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
                <DialogHeader>
                    <DialogTitle>Edit Project</DialogTitle>
                    <DialogDescription>
                        Update the project information and details.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 py-4">
                    {/* Basic Information Section */}
                    <div className="col-span-2 space-y-4">
                        <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2 space-y-2">
                                <Label htmlFor="editName">Project Name *</Label>
                                <Input
                                    id="editName"
                                    value={editingProject.name || ""}
                                    onChange={(e) => setEditingProject({ ...editingProject, name: e.target.value })}
                                    placeholder="Garden Redesign Project"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="editProjectType">Project Type *</Label>
                                <Select 
                                    value={editingProject.project_type || "construction"} 
                                    onValueChange={handleProjectTypeChange}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="design_only">Design Only</SelectItem>
                                        <SelectItem value="construction">Construction</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="editPriority">Priority</Label>
                                <Select 
                                    value={editingProject.priority || "medium"} 
                                    onValueChange={(value) => setEditingProject({ ...editingProject, priority: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low">Low</SelectItem>
                                        <SelectItem value="medium">Medium</SelectItem>
                                        <SelectItem value="high">High</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="editCompany">Company</Label>
                                <Select 
                                    value={editingProject.company || ""} 
                                    onValueChange={(value) => setEditingProject({ ...editingProject, company: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select company" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Landscaping">Landscaping</SelectItem>
                                        <SelectItem value="Exteriors">Exteriors</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    {/* Location Section */}
                    <div className="col-span-2 space-y-4">
                        <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Jobsite Location</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2 space-y-2">
                                <Label htmlFor="editAddress">Jobsite Address</Label>
                                <Input
                                    id="editAddress"
                                    value={editingProject.jobsite_address || ""}
                                    onChange={(e) => setEditingProject({ ...editingProject, jobsite_address: e.target.value })}
                                    placeholder="123 Main St"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="editCity">City</Label>
                                <Input
                                    id="editCity"
                                    value={editingProject.city || ""}
                                    onChange={(e) => setEditingProject({ ...editingProject, city: e.target.value })}
                                    placeholder="City"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="editZip">Zip Code</Label>
                                <Input
                                    id="editZip"
                                    value={editingProject.zip || ""}
                                    onChange={(e) => setEditingProject({ ...editingProject, zip: e.target.value })}
                                    placeholder="12345"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Financial Section */}
                    <div className="col-span-2 space-y-4">
                        <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Financial Details</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="editContractPrice">{editingProject.project_type === "design_only" ? "Design Fee *" : "Contract Price *"}</Label>
                                <Input
                                    id="editContractPrice"
                                    type="number"
                                    value={editingProject.contract_price || ""}
                                    onChange={(e) => setEditingProject({ ...editingProject, contract_price: e.target.value })}
                                    placeholder={editingProject.project_type === "design_only" ? "5000" : "75000"}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="editCrewPayrollEstimate">Crew Payroll Estimate</Label>
                                <Input
                                    id="editCrewPayrollEstimate"
                                    type="number"
                                    value={editingProject.crew_payroll_estimate || ""}
                                    onChange={(e) => setEditingProject({ ...editingProject, crew_payroll_estimate: e.target.value })}
                                    placeholder="15000"
                                />
                            </div>
                            {editingProject.project_type === "design_only" && (
                                <div className="space-y-2">
                                    <Label htmlFor="editEstimatedBudget">Estimated Construction Budget</Label>
                                    <Input
                                        id="editEstimatedBudget"
                                        type="number"
                                        value={editingProject.estimated_construction_budget || ""}
                                        onChange={(e) => setEditingProject({ ...editingProject, estimated_construction_budget: e.target.value })}
                                        placeholder="150000"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Timeline Section */}
                    <div className="col-span-2 space-y-4">
                        <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Project Timeline</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="editContractSignDate">Contract Sign Date</Label>
                                <Input
                                    id="editContractSignDate"
                                    type="date"
                                    value={formatDate(editingProject.contract_sign_date)}
                                    onChange={(e) => setEditingProject({ ...editingProject, contract_sign_date: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="editStartDate">Start Date</Label>
                                <Input
                                    id="editStartDate"
                                    type="date"
                                    value={formatDate(editingProject.start_date)}
                                    onChange={(e) => setEditingProject({ ...editingProject, start_date: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="editEndDate">Est. End Date</Label>
                                <Input
                                    id="editEndDate"
                                    type="date"
                                    value={formatDate(editingProject.end_date)}
                                    onChange={(e) => setEditingProject({ ...editingProject, end_date: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Team Assignment removed — designers, crews, subs are all assigned from the project view dialog */}

                    {/* Additional Details Section */}
                    <div className="col-span-2 space-y-4">
                        <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Additional Details</h3>
                        <div className="space-y-2">
                            <Label htmlFor="editDescription">Project Description</Label>
                            <Textarea
                                id="editDescription"
                                value={editingProject.description || ""}
                                onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })}
                                placeholder="Describe the landscaping project..."
                                rows={3}
                            />
                        </div>
                    </div>
                </div>
                
                <DialogFooter>
                    <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleUpdateProject}
                        disabled={updating || !editingProject?.name}
                        className="bg-brand-600 hover:bg-brand-700"
                    >
                        {updating ? "Updating..." : "Update Project"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {/* Project Type Change Confirmation Dialog */}
        <Dialog open={showProjectTypeConfirm} onOpenChange={setShowProjectTypeConfirm}>
            <DialogContent className="sm:max-w-[450px]">
                <DialogHeader>
                    <DialogTitle>Change Project Type?</DialogTitle>
                </DialogHeader>
                <div className="py-4">
                    <p className="text-sm text-gray-600 mb-3">
                        Changing the project type will reset the payment schedule:
                    </p>
                    <ul className="ml-6 text-sm text-gray-600 list-disc space-y-2">
                        {pendingProjectType === "design_only" ? (
                            <li>Current payment schedule will be replaced with a single <strong>"Design Fee"</strong> payment</li>
                        ) : (
                            <li>Current payment schedule will be replaced with 4 construction payments: <strong>Deposit, 1st Progress, 2nd Progress, and Final Payment</strong></li>
                        )}
                        <li>Payment statuses and dates will be preserved where possible</li>
                        <li>This action cannot be undone</li>
                    </ul>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={cancelProjectTypeChange}>
                        Cancel
                    </Button>
                    <Button onClick={confirmProjectTypeChange} className="bg-brand-600 hover:bg-brand-700">
                        Change Type
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </>
    );
};

export default EditProjectDialog;